import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, message, Typography, Tag } from "antd";
import {
  FileText,
  Video,
  Link as LinkIcon,
  ChevronLeft,
  Eye,
  ArrowLeft,
  Edit,
} from "lucide-react";
import { getProjectById } from "../services/projectService";

const { Text } = Typography;

interface Project {
  _id: string;
  title: string;
  description: string;
  industry?: string;
  demoLink: string;
  documentUrl?: string;
  documentOriginalName?: string;
  sourceSubmissionId?: {
    documentOriginalName?: string;
  };
  supportingLinks?: Array<{ url: string; description?: string }>;
  googleTechnologies?: string[];
  otherTechnologies?: string[];
  ownerName?: string;
  ownerEmail?: string;
  isFeatured?: boolean;
  isVisible?: boolean;
  documentation?: string;
  createdAt?: string;
  updatedAt?: string;
}

const ProjectsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await getProjectById(id!);
      if (response.success) {
        setProject(response.data);
      } else {
        message.error("Failed to load project details");
        navigate("/projects");
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to load project details"
      );
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <Button
          icon={<ArrowLeft />}
          onClick={() => navigate("/projects")}
          className="mb-4"
        >
          Back
        </Button>
        <div className="text-center py-12">
          <Text type="secondary">Project not found</Text>
        </div>
      </div>
    );
  }

  const allTechnologies = [
    ...(project.googleTechnologies || []),
    ...(project.otherTechnologies || []),
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <Button
          type="text"
          icon={<ChevronLeft size={16} />}
          onClick={() => navigate("/projects")}
          className="p-2 text-black text-base font-medium"
        >
          Back
        </Button>
        <Button
          type="primary"
          icon={<Edit size={16} />}
          onClick={() => navigate(`/projects/edit/${id}`)}
          className="bg-[#010ED0] hover:bg-[#010ED0]/80 border-0"
        >
          Edit Project
        </Button>
      </div>

      {/* Project Details */}
      <div className="bg-white rounded-[8px] border border-gray-200 p-6 mb-6 w-full max-w-4xl mx-auto flex flex-col gap-5">
        <div className="text-2xl font-semibold text-gray-900 ">
          {project.title.charAt(0).toUpperCase() + project.title.slice(1)}
        </div>

        {project.ownerName && (
          <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
            <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
              Owner Information:
            </div>

            <div className="grid grid-cols-2 ">
              <div className="flex flex-col gap-1">
                <span className="text-base font-medium text-[#111112]">
                  Owner Name
                </span>
                <span className="text-sm font-normal text-[#475467]">
                  {project.ownerName} 
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-base font-medium text-[#111112]">
                  Owner Email
                </span>
                <span className="text-sm font-normal text-[#475467]">
                  {project.ownerEmail}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Project Description
          </div>

          <span className="text-base font-normal text-[#475467]">
            {project.description}
          </span>
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Others Details
          </div>

          <div className="grid grid-cols-2 ">
            <div className="flex flex-col gap-1">
              <span className="text-base font-medium text-[#111112]">
                Industry
              </span>
              <span className="text-sm font-normal text-[#475467]">
                {project.industry || "N/A"}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-base font-medium text-[#111112]">
                Status
              </span>
              <span className="text-sm font-normal text-[#475467]">
                {project.isFeatured && (
                  <span className="px-2 py-1 border border-[#B9E6FE] bg-[#F0F9FF] text-[#026AA2] rounded-full text-xs font-medium mr-2 inline-block">
                    Featured
                  </span>
                )}
                {project.isVisible ? (
                  <span className="px-2 py-1 border border-[#ABEFC6] bg-[#ECFDF3] text-[#067647] rounded-full text-xs font-medium inline-block">
                    Visible
                  </span>
                ) : (
                  <span className="px-2 py-1 border border-[#E2E8F0] bg-[#F8FAFC] text-[#475569] rounded-full text-xs font-medium inline-block">
                    Hidden
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Technologies (Google and Others)
          </div>

          <div className="flex flex-wrap gap-2">
            {allTechnologies.map((tech, index) => (
              <Tag key={index} className="px-3 py-1 text-sm">
                {tech}
              </Tag>
            ))}
          </div>
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Project Materials
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-start gap-3">
                  <div className="h-full flex items-start justify-start">
                    <Video className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <Text strong>Demo Video</Text>
                    <br />
                    <Text type="secondary" className="text-sm">
                      {project.demoLink}
                    </Text>
                  </div>
                </div>
                <div
                  onClick={() => window.open(project.demoLink, "_blank")}
                  className="cursor-pointer"
                >
                  <Eye />
                </div>
              </div>
            </div>

            {project.documentUrl && (
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 w-full justify-between">
                  <div className="flex items-start gap-3">
                    <div className="h-full flex items-start justify-start">
                      <FileText className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <Text strong>Pitch Deck (PDF)</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {project.documentOriginalName || 
                         (project.sourceSubmissionId as any)?.documentOriginalName || 
                         "Document"}
                      </Text>
                    </div>
                  </div>
                  <div
                    onClick={() => window.open(project.documentUrl, "_blank")}
                    className="cursor-pointer"
                  >
                    <Eye />
                  </div>
                </div>
              </div>
            )}

            {project.supportingLinks &&
              project.supportingLinks.length > 0 && (
                <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg bg-[#F9FAFB]">
                  <div className="h-full flex items-center justify-start gap-2">
                    <LinkIcon className="w-5 h-5 text-gray-500" />
                    <Text strong>Supporting Links</Text>
                  </div>
                  {project.supportingLinks.map((link, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex flex-col gap-2 w-full justify-between">
                        <div className="flex items-center justify-between w-full gap-3">
                          <div>
                            <Text strong>
                              {link.description || "Supporting Link"}
                            </Text>
                            <br />
                            <Text type="secondary" className="text-sm">
                              {link.url}
                            </Text>
                          </div>

                          <div
                            onClick={() => window.open(link.url, "_blank")}
                            className="cursor-pointer"
                          >
                            <Eye />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>

        {/* Project Documentation Section */}
        {project.documentation && (
          <div className="bg-white rounded-[8px] border border-gray-200 p-6 mb-6 w-full max-w-4xl mx-auto flex flex-col gap-5">
            <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
              Project Documentation
            </div>
            <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100">
              <pre className="whitespace-pre-wrap font-sans text-sm text-[#475467] overflow-x-auto">
                {project.documentation}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsDetails;

